// Plugin Registry - singleton for managing plugins
// Based on backend registry (nre/backend/internal/plugin/registry.go)

import {
  PluginManifest,
  PluginRouteWithMeta,
  NavigationItem,
  DashboardWidgetWithMeta,
  MenuItemWithMeta,
  DeepLinkParams,
} from './types';

class PluginRegistryImpl {
  private plugins: Map<string, PluginManifest> = new Map();

  register(manifest: PluginManifest): void {
    this.validateManifest(manifest);
    
    if (this.plugins.has(manifest.id)) {
      throw new Error(`Plugin with id "${manifest.id}" is already registered`);
    }
    
    this.plugins.set(manifest.id, manifest);
    console.log(`=== PluginRegistry REGISTERED: ${manifest.id} v${manifest.version} ===`);
    console.log('Total plugins now:', this.plugins.size);
  }

  private validateManifest(manifest: PluginManifest): void {
    if (!manifest.id || typeof manifest.id !== 'string') {
      throw new Error('Plugin manifest must have a valid id');
    }
    if (!manifest.name || typeof manifest.name !== 'string') {
      throw new Error('Plugin manifest must have a valid name');
    }
    if (!manifest.basePath || !manifest.basePath.startsWith('/')) {
      throw new Error('Plugin manifest must have a valid basePath starting with /');
    }
    if (!Array.isArray(manifest.routes)) {
      throw new Error('Plugin manifest must have routes array');
    }
  }

  getPlugins(): PluginManifest[] {
    return Array.from(this.plugins.values());
  }

  getAll(): PluginManifest[] {
    return Array.from(this.plugins.values());
  }

  getPlugin(id: string): PluginManifest | undefined {
    return this.plugins.get(id);
  }

  getRoutes(): PluginRouteWithMeta[] {
    const routes: PluginRouteWithMeta[] = [];
    
    for (const plugin of this.plugins.values()) {
      for (const route of plugin.routes) {
        const fullPath = `${plugin.basePath}${route.path}`.replace(/\/+/g, '/');
        routes.push({
          ...route,
          pluginId: plugin.id,
          fullPath,
        });
      }
    }
    
    return routes;
  }

  getNavigationItems(currentPath: string): NavigationItem[] {
    for (const plugin of this.plugins.values()) {
      if (currentPath.startsWith(plugin.basePath)) {
        return plugin.navigationItems || [];
      }
    }
    return [];
  }

  getPluginForPath(currentPath: string): PluginManifest | null {
    for (const plugin of this.plugins.values()) {
      if (currentPath.startsWith(plugin.basePath)) {
        return plugin;
      }
    }
    return null;
  }

  getDashboardWidgets(): DashboardWidgetWithMeta[] {
    const widgets: DashboardWidgetWithMeta[] = [];
    
    for (const plugin of this.plugins.values()) {
      if (plugin.dashboardWidgets) {
        for (const widget of plugin.dashboardWidgets) {
          widgets.push({
            ...widget,
            pluginId: plugin.id,
            pluginName: plugin.name,
          });
        }
      }
    }
    
    return widgets;
  }

  getMenuItems(): MenuItemWithMeta[] {
    const items: MenuItemWithMeta[] = [];
    
    for (const plugin of this.plugins.values()) {
      if (plugin.menuItems) {
        for (const item of plugin.menuItems) {
          items.push({
            ...item,
            pluginId: plugin.id,
          });
        }
      }
    }
    
    return items;
  }

  handleDeepLink(startParam: string): { pluginId: string; params: DeepLinkParams } | null {
    if (!startParam) return null;
    
    // Deep link format: {pluginId}_{action}_{params}
    const parts = startParam.split('_');
    if (parts.length < 2) return null;
    
    const pluginId = parts[0];
    const plugin = this.plugins.get(pluginId);
    
    if (plugin?.deepLinkHandler) {
      const params = plugin.deepLinkHandler(startParam);
      if (params) {
        return { pluginId, params };
      }
    }
    
    return null;
  }
}

// Singleton instance
export const PluginRegistry = new PluginRegistryImpl();
